import { z } from 'zod';
import { exportFileSourceSchema, exportFileStatusSchema, ftpAccountSchema, } from '../status/file.js';
import { dateRangeQuerySchema, isoDateTimeSchema, objectIdSchema, paginationQuerySchema, sortQuerySchema, } from './common.js';
/** Ligne de l'écran « Fichiers CSV et envois » (`02` §5). */
export const exportFileListItemSchema = z.object({
    id: objectIdSchema,
    name: z.string(),
    source: exportFileSourceSchema,
    store: z.string(),
    ftpAccount: ftpAccountSchema,
    orderId: objectIdSchema.nullable(),
    orderName: z.string().nullable(),
    invoiceId: objectIdSchema.nullable(),
    invoiceNumber: z.string().nullable(),
    status: exportFileStatusSchema,
    size: z.number().int().min(0),
    attempts: z.number().int().min(0),
    lastError: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    queuedAt: isoDateTimeSchema.nullable(),
    sentAt: isoDateTimeSchema.nullable(),
    uploadedBy: objectIdSchema.nullable(),
    /** `false` quand le contenu a été purgé après la rétention (`03` §2). */
    hasContent: z.boolean(),
});
export const exportFileListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    ...dateRangeQuerySchema.shape,
    source: exportFileSourceSchema.optional(),
    ftpAccount: ftpAccountSchema.optional(),
    status: z.array(exportFileStatusSchema).optional(),
    store: z.string().optional(),
    search: z.string().min(1).optional(),
});
export const exportFileDetailSchema = z.object({
    ...exportFileListItemSchema.shape,
    sha256: z.string(),
    content: z.string().nullable(),
    expiresAt: isoDateTimeSchema.nullable(),
});
/**
 * Contrôles affichés avant l'envoi d'un CSV déposé manuellement
 * (`01` §4 « Uploader un CSV », `02` §5).
 */
export const manualUploadCheckSchema = z.object({
    fileName: z.string(),
    /** L'extension du fichier est-elle `.csv` ? (`01` §4, contrôle bloquant). */
    extensionValid: z.boolean(),
    nameValid: z.boolean(),
    suggestedName: z.string().nullable(),
    firstLine: z.string(),
    firstLineValid: z.boolean(),
    delimiterValid: z.boolean(),
    encodingValid: z.boolean(),
    headerCount: z.number().int().min(0),
    lineCount: z.number().int().min(0),
    errors: z.array(z.string()),
    /**
     * `true` quand un contrôle de contenu échoue (extension, première ligne,
     * séparateur, encodage) : l'envoi est refusé. Un nom non conforme à lui seul
     * ne bloque pas — un renommage est proposé (`suggestedName`) mais jamais
     * appliqué de force (`01` §4).
     */
    blocking: z.boolean(),
});
/**
 * Aucun séparateur de chemin ni `..` (`04` §6, HIGH-1) : `fileName` est
 * concaténé plus loin par `martelet-ftp/client.ts::joinRemotePath` pour
 * former le chemin distant de l'upload FTP Martelet, un `/`, un `\` ou une
 * traversée (`../../../evil.csv`) écrirait alors hors de `/IN`. Rejeté ici,
 * à la frontière de la requête, en plus du contrôle bloquant équivalent dans
 * `checkManualUpload` (`flows/maintenance/manual-upload.ts`) et du filet de
 * sécurité final dans `joinRemotePath` lui-même — défense en profondeur sur
 * les trois couches.
 */
const SAFE_FILE_NAME_PATTERN = /^[A-Za-z0-9._-]+$/;
export const manualUploadBodySchema = z.object({
    fileName: z
        .string()
        .min(1)
        .max(255)
        .regex(SAFE_FILE_NAME_PATTERN, 'Nom de fichier invalide.')
        .refine((name) => !name.includes('..'), { message: 'Nom de fichier invalide.' }),
    ftpAccount: ftpAccountSchema,
    /** Contenu du fichier, encodé en base64 pour traverser JSON sans perte. */
    contentBase64: z.string().min(1),
});
/**
 * Résultat de `POST /api/files/upload` (`02` §5) : le contrôle de validité est
 * toujours renvoyé, que l'envoi ait eu lieu ou non (`uploaded: false` quand un
 * contrôle bloquant a refusé le dépôt).
 */
export const manualUploadResultSchema = z.object({
    uploaded: z.boolean(),
    fileId: objectIdSchema.nullable(),
    fileName: z.string(),
    check: manualUploadCheckSchema,
});
/** Résultat de `POST /api/files/flush` (`01` §8, `02` §2 et §5). */
export const flushQueueResultSchema = z.object({
    queuedCount: z.number().int().min(0),
    ftpAccounts: z.array(ftpAccountSchema),
    jobIds: z.array(z.string()),
    testMode: z.boolean().optional(),
});
/** Résumé de la file d'attente de maintenance (`02` §2 et §5). */
export const queueSummarySchema = z.object({
    queuedCount: z.number().int().min(0),
    oldestQueuedAt: isoDateTimeSchema.nullable(),
});
//# sourceMappingURL=files.js.map